import { forwardRef, useRef, useState, useEffect } from 'react';
import { logDsxWarning } from '../util/logging.js';
import styles from './Dialog.module.scss.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { use_UNSAFEUserAgentInfo } from '../util/use_UNSAFEUserAgent.js';
import { combineRefs } from '../util/combineRefs.js';
import { A11yProvider } from '../provider/A11yProvider.js';
import classNames from 'clsx';

function openDialog(dialog, modal, focusOnOpen) {
  if (modal) {
    dialog.showModal();
  } else {
    if (!focusOnOpen) {
      /*
      A pile of hacks to make sure that the currently focused element doesn't change as a
      result of calling dialog.show().
      As of 2024-02 it seems only Chrome needs the event prevention; setting `inert` seems
      sufficient for other browsers. Nobody's following the spec so this is all Calvinball.
      */
      const focused = document.activeElement;
      const preventEvent = event => {
        event.stopPropagation();
      };
      document.addEventListener('focusout', preventEvent, true);
      document.addEventListener('focusin', preventEvent, true);
      dialog.inert = true;
      dialog.show();
      if (focused && document.activeElement !== focused) {
        focused.focus({
          preventScroll: true
        });
      }
      document.removeEventListener('focusout', preventEvent, true);
      document.removeEventListener('focusin', preventEvent, true);
      dialog.inert = false;
    } else {
      dialog.show();
    }
  }
}
function closeDialog(dialog, closeAnimationClassName) {
  if (closeAnimationClassName) {
    dialog.classList.add(closeAnimationClassName);
    const listener = () => {
      dialog.removeEventListener('animationend', listener);
      dialog.close();
      dialog.classList.remove(closeAnimationClassName);
    };
    dialog.addEventListener('animationend', listener);
  } else {
    dialog.close();
  }
}

/**
 * A shared counter of the number of times that the no-scroll class has been added to `html`.
 */
let noscrollCount = 0;

/**
 * Simple wrapper around the `<dialog />` element that utilizes the `show()`/`showModal()` methods
 * per [MDN recommendations](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog).
 */
const Dialog = /*#__PURE__*/forwardRef(function Dialog(_ref, forwardedRef) {
  let {
    children,
    className: callerClassName,
    closeAnimationClassName,
    focusOnOpen = true,
    isOpen,
    modal = false,
    mode = 'default',
    onClose,
    onCloseRequested,
    ...props
  } = _ref;
  const ref = useRef(null);
  const mouseDownWithin = useRef(false);
  const className = classNames('dsx-component', callerClassName, styles['dialog']);
  const modalRef = useRef(false);

  // useState instead of useRef is used to guarantee a rerender with the portal target when it is initialized
  const [portalTarget, setPortalTarget] = useState(null);
  useEffect(() => {
    if (isOpen && ref.current && !ref.current.open) {
      modalRef.current = modal;
      openDialog(ref.current, modal, focusOnOpen);
    }
    if (!isOpen && ref.current && ref.current.open) {
      closeDialog(ref.current, closeAnimationClassName);
    }

    // This effect only needs to run when isOpen has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    if (ref.current && modalRef.current && isOpen) {
      ++noscrollCount;
      const html = document.querySelector('html');
      html.classList.add(styles['no-scroll']);
      return () => {
        --noscrollCount;
        if (noscrollCount === 0) {
          html.classList.remove(styles['no-scroll']);
        }
      };
    }
  }, [isOpen]);
  useEffect(() => {
    if (ref.current?.open && modal !== modalRef.current) {
      logDsxWarning('Dialog', 'changing the value of the `modal` prop while a dialog is open is not supported.');
    }
  }, [modal]);
  useEffect(() => {
    return () => {
      if (isOpen && closeAnimationClassName) {
        logDsxWarning('Dialog', 'unmounting an open Dialog will not perform the closing animation.');
      }
    };
    // This effect should only run on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (mode === 'default') {
        const shouldClose = onCloseRequested?.() ?? true;
        if (shouldClose) {
          closeDialog(event.currentTarget, closeAnimationClassName);
          onClose?.(event);
        }
      }
    }
    props.onKeyDown?.(event);
  };
  const onMouseDown = event => {
    if (!ref.current) {
      return;
    }
    const dialogRect = ref.current.getBoundingClientRect();
    mouseDownWithin.current = isWithin(dialogRect, event);
  };
  const onMouseUp = event => {
    if (!ref.current) {
      return;
    }

    // when dialogs are nested, the target can be checked to narrow down the
    // specific dialog that should be closed.
    const forThisDialog = event.target === ref.current;

    // the dialog reports mouseup events on the backdrop as well as the dialog itself,
    // the bounding rect is used to see exactly where the click was.
    const dialogRect = ref.current.getBoundingClientRect();
    const mouseUpWithin = isWithin(dialogRect, event);
    const readyToClose = mode === 'default' && forThisDialog && !mouseDownWithin.current && !mouseUpWithin;
    if (readyToClose) {
      const shouldClose = onCloseRequested?.() ?? true;
      if (shouldClose) {
        closeDialog(event.currentTarget, closeAnimationClassName);
        onClose?.(event);
      }
    }
  };

  // https://www.matuzo.at/blog/2023/focus-dialog/
  // Browser behavior regarding <dialog> and focus is inconsistent.
  // The spec says: "The tabindex attribute must not be specified on dialog elements."
  // The blog post says: "I won't do what you tell me.  – Rage Against the Machine"
  // Most browsers set a default tabIndex of -1 (if at all).
  // Firefox sets a default tabIndex of 0, which is definitely not what we want.
  const ua = use_UNSAFEUserAgentInfo();
  const tabIndex = ua.browser === 'firefox' ? -1 : undefined;
  return /*#__PURE__*/jsxs("dialog", {
    className: className,
    ref: combineRefs(forwardedRef, ref),
    tabIndex: tabIndex,
    onKeyDown: onKeyDown,
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    ...props,
    children: [/*#__PURE__*/jsx(A11yProvider, {
      portalTarget: portalTarget,
      children: children
    }), /*#__PURE__*/jsx("div", {
      ref: setPortalTarget
    })]
  });
});
function isWithin(rect, event) {
  return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
}

export { Dialog };
